/**
 * Regla: si un banco rechazó la solicitud de crédito de una propiedad, el
 * cliente elige OTRO banco y no puede volver a elegir el que lo rechazó.
 *
 * La fuente de verdad es la BD (migración 20261004142000):
 * - `bancos_disponibles_para_cuenta(p_id_cuenta_cobranza)`: bancos de convenio
 *   activos con `disponible` (false = ya rechazó la propiedad).
 * - Guard en `bancos_solicitudes` con SQLSTATE `BR001` al crear o reactivar
 *   una solicitud con ese banco. PostgREST lo devuelve como HTTP 500 con el
 *   código en el body: se traduce por código, no por status.
 *
 * Solo bloquea un rechazo del banco: desistido o expirada no bloquean.
 */

export const SQLSTATE_BANCO_RECHAZADO = "BR001";

/** Etiqueta del banco que rechazó, en el selector del cliente. */
export const ETIQUETA_RECHAZO_CLIENTE = "Rechazó tu solicitud";

/** Etiqueta del banco que rechazó, en los selectores del equipo SOZU. */
export const ETIQUETA_RECHAZO_STAFF = "Rechazó la solicitud de esta propiedad";

/** Aviso al cliente si intenta (o el servidor rechaza) ese banco. */
export const AVISO_BANCO_RECHAZADO = "Este banco ya rechazó tu solicitud. Elige otro banco.";

/** Estado vacío: todos los bancos con convenio ya rechazaron. */
export const SIN_BANCOS_DISPONIBLES =
  "Los bancos con convenio ya revisaron tu solicitud y no la aprobaron. Tu asesor SOZU te ayudará a buscar otra opción de pago.";

/** Fila de `bancos_disponibles_para_cuenta`. */
export interface BancoDisponibleRow {
  id_banco: number | string;
  disponible: boolean | null;
  fecha_rechazo?: string | null;
}

/** ¿El error de supabase-js es el guard del banco que rechazó? */
export function esErrorBancoRechazado(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { code?: unknown }).code === SQLSTATE_BANCO_RECHAZADO;
}

/** Ids de los bancos que ya rechazaron la solicitud de la propiedad. */
export function idsBancosRechazados(rows: BancoDisponibleRow[] | null | undefined): Set<number> {
  const ids = new Set<number>();
  for (const r of rows ?? []) if (r.disponible === false) ids.add(Number(r.id_banco));
  return ids;
}

/**
 * Marca cada banco con `rechazo`. Los que rechazaron siguen en la lista (se
 * pintan deshabilitados) pero al final: lo primero es lo que sí se puede elegir.
 */
export function marcarBancosRechazados<T extends { id_banco: number }>(
  bancos: T[],
  rechazados: Set<number>,
): (T & { rechazo: boolean })[] {
  const marcados = bancos.map((b) => ({ ...b, rechazo: rechazados.has(b.id_banco) }));
  return [...marcados.filter((b) => !b.rechazo), ...marcados.filter((b) => b.rechazo)];
}

/** ¿Queda al menos un banco que se pueda elegir? */
export function hayBancoDisponible(bancos: { rechazo: boolean }[]): boolean {
  return bancos.some((b) => !b.rechazo);
}

/** Fecha del rechazo por banco (null si la BD no la trae), solo de los que rechazaron. */
export function rechazosPorBanco(rows: BancoDisponibleRow[] | null | undefined): Map<number, string | null> {
  const m = new Map<number, string | null>();
  for (const r of rows ?? []) if (r.disponible === false) m.set(Number(r.id_banco), r.fecha_rechazo ?? null);
  return m;
}
